import { UseQueryResult, useQuery } from '@tanstack/react-query';

import { withGlobalError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';
import axios, { parseAxiosError } from '@/portainer/services/axios';

import { queryKeys } from './query-keys';

/**
 * @returns a custom resource from the Kubernetes API proxy
 * when yaml is set to true, the expected return type is a string
 */
export function useCustomResource<T = unknown>(
  environmentId: EnvironmentId,
  namespace: string,
  name: string,
  apiVersion: string,
  plural: string,
  options?: { autoRefreshRate?: number; yaml?: boolean }
): UseQueryResult<T> {
  return useQuery(
    queryKeys.customResource(
      environmentId,
      namespace,
      name,
      apiVersion,
      plural,
      options?.yaml
    ),
    () =>
      getCustomResource<T>(
        environmentId,
        namespace,
        name,
        apiVersion,
        plural,
        options?.yaml
      ),
    {
      enabled:
        !!environmentId && !!namespace && !!name && !!apiVersion && !!plural,
      ...withGlobalError('Unable to retrieve custom resource'),
      refetchInterval() {
        return options?.autoRefreshRate ?? false;
      },
    }
  );
}

async function getCustomResource<T = unknown>(
  environmentId: EnvironmentId,
  namespace: string,
  name: string,
  apiVersion: string,
  plural: string,
  yaml?: boolean
): Promise<T> {
  try {
    const { data } = await axios.get<T>(
      `/kubernetes/${environmentId}/customresource`,
      {
        params: {
          namespace,
          name,
          apiVersion,
          plural,
          format: yaml ? 'yaml' : 'json',

          // this logic is to get the latest YAML response
          // axios-cache-adapter looks for the response headers to determine if the response should be cached
          // to avoid writing requestInterceptor, adding a query param to the request url
          ...(yaml && {
            _: Date.now(),
          }),
        },
        headers: { Accept: yaml ? 'application/yaml' : 'application/json' },
      }
    );
    return data;
  } catch (err) {
    throw parseAxiosError(err);
  }
}
